import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Form, 
  Select, 
  Input, 
  Switch, 
  InputNumber, 
  DatePicker, 
  TimePicker,
  Row, 
  Col, 
  Typography, 
  Space, 
  Divider, 
  Alert, 
  Spin, 
  message,
  Tag,
  Tooltip,
  Popconfirm,
  Tabs,
  Collapse,
  Checkbox
} from 'antd';
import { 
  SaveOutlined, 
  DeleteOutlined, 
  PlusOutlined, 
  MinusCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  MedicineBoxOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useProviders, useCreateSchedule, useUpdateSchedule, useDeleteSchedule, useGraphQLError } from '../hooks/useGraphQL';
import { useTranslation } from '../hooks/useTranslation';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { TabPane } = Tabs;

interface TimeSlot {
  from: string;
  to: string;
}

interface WorkingHours {
  monday?: TimeSlot[];
  tuesday?: TimeSlot[];
  wednesday?: TimeSlot[];
  thursday?: TimeSlot[];
  friday?: TimeSlot[];
  saturday?: TimeSlot[];
  sunday?: TimeSlot[];
}

interface ProviderSchedule {
  id: string;
  provider_id: string;
  status: string;
  from_date?: string;
  to_date?: string;
  schedule?: WorkingHours;
  timezone?: string;
  priority: number;
  default_appointment_duration?: number;
  max_appointments_per_day?: number;
  is_available_for_walk_in: boolean;
  is_available_for_telemedicine: boolean;
  advance_booking_days?: number;
  same_day_booking: boolean;
  min_notice_hours?: number;
  max_notice_days?: number;
  cancellation_hours?: number;
  reschedule_hours?: number;
  created_at: string;
  updated_at: string;
}

interface Provider {
  id: string;
  doctor_id: string;
  clinic_id: string;
  consultation_fee: number;
  department: string;
  employment_type: string;
  status: string;
  doctor: {
    id: string;
    first_name_en: string;
    last_name_en: string;
    first_name_zh_hk: string;
    last_name_zh_hk: string;
    license_number: string;
    specialization: string;
    status: string;
  };
  clinic: {
    id: string;
    name_en: string;
    name_zh_hk: string;
    internal_name?: string;
    address: string;
    region: string;
    district: string;
  };
  schedules: ProviderSchedule[];
}

const DoctorSchedule: React.FC = () => {
  const { t } = useTranslation();
  const { doctorId } = useParams<{ doctorId: string }>();
  const navigate = useNavigate();
  
  const { data, loading, error } = useProviders();
  const [createSchedule] = useCreateSchedule();
  const [updateSchedule] = useUpdateSchedule();
  const [deleteSchedule] = useDeleteSchedule();
  const { handleError } = useGraphQLError();

  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ProviderSchedule | null>(null);
  const [activeTab, setActiveTab] = useState('general');

  const providers = data?.providers || [];
  const doctorProviders = providers.filter((provider: Provider) => 
    provider.doctor_id === doctorId
  );

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30'
  ];

  const weekDays = [
    { key: 'monday', label: t('schedule.monday'), icon: '1️⃣' },
    { key: 'tuesday', label: t('schedule.tuesday'), icon: '2️⃣' },
    { key: 'wednesday', label: t('schedule.wednesday'), icon: '3️⃣' },
    { key: 'thursday', label: t('schedule.thursday'), icon: '4️⃣' },
    { key: 'friday', label: t('schedule.friday'), icon: '5️⃣' },
    { key: 'saturday', label: t('schedule.saturday'), icon: '6️⃣' },
    { key: 'sunday', label: t('schedule.sunday'), icon: '7️⃣' }
  ];

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'success', text: t('schedule.active'), icon: <CheckCircleOutlined /> },
      inactive: { color: 'warning', text: t('schedule.inactive'), icon: <ExclamationCircleOutlined /> },
      draft: { color: 'default', text: t('schedule.draft'), icon: <InfoCircleOutlined /> },
      expired: { color: 'error', text: t('schedule.expired'), icon: <StopOutlined /> }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const handleCreateSchedule = async (values: any) => {
    if (!doctorProviders.length) {
      message.error(t('schedule.noProviderFound'));
      return;
    }

    try {
      setIsSubmitting(true);
      await createSchedule({
        variables: {
          providerId: doctorProviders[0].id,
          createScheduleDto: {
            ...values,
            schedule: formatScheduleForSubmission(values.schedule)
          }
        }
      });
      form.resetFields();
      message.success(t('schedule.scheduleCreated'));
    } catch (error) {
      console.error('Error creating schedule:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSchedule = async (scheduleId: string, values: any) => {
    try {
      setIsSubmitting(true);
      await updateSchedule({
        variables: {
          id: scheduleId,
          updateScheduleDto: {
            ...values,
            schedule: formatScheduleForSubmission(values.schedule)
          }
        }
      });
      setEditingSchedule(null);
      form.resetFields();
      message.success(t('schedule.scheduleUpdated'));
    } catch (error) {
      console.error('Error updating schedule:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    try {
      await deleteSchedule({
        variables: { id: scheduleId }
      });
      message.success(t('schedule.scheduleDeleted'));
    } catch (error) {
      console.error('Error deleting schedule:', error);
      message.error(handleError(error));
    }
  };

  const formatScheduleForSubmission = (schedule: any) => {
    const formatted: WorkingHours = {};
    Object.keys(schedule).forEach(day => {
      if (schedule[day] && schedule[day].length > 0) {
        formatted[day as keyof WorkingHours] = schedule[day].map((slot: any) => ({
          from: slot.from,
          to: slot.to
        }));
      }
    });
    return formatted;
  };

  const formatScheduleForForm = (schedule: WorkingHours) => {
    const formatted: any = {};
    weekDays.forEach(day => {
      formatted[day.key] = schedule[day.key as keyof WorkingHours] || [];
    });
    return formatted;
  };

  const handleEditSchedule = (schedule: ProviderSchedule) => {
    setEditingSchedule(schedule);
    const formData = {
      ...schedule,
      schedule: formatScheduleForForm(schedule.schedule || {}),
      from_date: schedule.from_date ? dayjs(schedule.from_date) : undefined,
      to_date: schedule.to_date ? dayjs(schedule.to_date) : undefined
    };
    form.setFieldsValue(formData);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('schedule.loadingSchedules')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Card>
          <Alert
            message={t('schedule.errorLoadingSchedules')}
            description={handleError(error)}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (!doctorProviders.length) {
    return (
      <div>
        <Card>
          <Alert
            message={t('schedule.noProviderFound')}
            description={t('schedule.noProviderDescription')}
            type="warning"
            showIcon
            action={
              <Button type="primary" onClick={() => navigate('/providers')}>
                {t('schedule.manageProviders')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const doctor = doctorProviders[0].doctor;
  const clinic = doctorProviders[0].clinic;

  return (
    <div>
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <MedicineBoxOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 12 }} />
              <Title level={2} style={{ margin: 0 }}>{t('schedule.editDoctorSchedule')}</Title>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div>
                <Text strong>{doctor.first_name_en} {doctor.last_name_en}</Text>
                <br />
                <Text type="secondary">{doctor.first_name_zh_hk} {doctor.last_name_zh_hk}</Text>
              </div>
              <Divider type="vertical" />
              <div>
                <Text strong>{clinic.internal_name || clinic.name_zh_hk || clinic.name_en}</Text>
                <br />
                <Text type="secondary">{clinic.name_zh_hk}</Text>
              </div>
              <Divider type="vertical" />
              <div>
                <EnvironmentOutlined style={{ marginRight: 4 }} />
                <Text type="secondary">{clinic.district}, {clinic.region}</Text>
              </div>
            </div>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => navigate('/doctors')}>
                {t('common.back')}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                setEditingSchedule(null);
                form.resetFields();
              }}>
                {t('schedule.addNewSchedule')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={24}>
        {/* Schedule Form */}
        <Col xs={24} lg={16}>
          <Card title={editingSchedule ? t('schedule.editSchedule') : t('schedule.createNewSchedule')}>
            <Form
              form={form}
              layout="vertical"
              onFinish={(values) => {
                if (editingSchedule) {
                  handleUpdateSchedule(editingSchedule.id, values);
                } else {
                  handleCreateSchedule(values);
                }
              }}
              initialValues={{
                status: 'active',
                priority: 0,
                default_appointment_duration: 30,
                max_appointments_per_day: 20,
                is_available_for_walk_in: false,
                is_available_for_telemedicine: true,
                advance_booking_days: 30,
                same_day_booking: true,
                min_notice_hours: 2,
                max_notice_days: 30,
                cancellation_hours: 24,
                reschedule_hours: 24,
                timezone: 'Asia/Hong_Kong'
              }}
            >
              <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab={t('schedule.generalSettings')} key="general">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('common.status')}
                        name="status"
                        rules={[{ required: true, message: t('schedule.pleaseSelectStatus') }]}
                      >
                        <Select>
                          <Option value="active">{t('schedule.active')}</Option>
                          <Option value="inactive">{t('schedule.inactive')}</Option>
                          <Option value="draft">{t('schedule.draft')}</Option>
                          <Option value="expired">{t('schedule.expired')}</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.priority')}
                        name="priority"
                        rules={[{ required: true, message: t('schedule.pleaseInputPriority') }]}
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.fromDate')}
                        name="from_date"
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.toDate')}
                        name="to_date"
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.timezone')}
                        name="timezone"
                        rules={[{ required: true, message: t('schedule.pleaseSelectTimezone') }]}
                      >
                        <Select>
                          <Option value="Asia/Hong_Kong">Asia/Hong_Kong (UTC+8)</Option>
                          <Option value="UTC">UTC</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tab={t('schedule.workingHours')} key="hours">
                  <Form.Item
                    label={t('schedule.weeklySchedule')}
                    name="schedule"
                  >
                    <Collapse defaultActiveKey={['monday', 'tuesday', 'wednesday', 'thursday', 'friday']}>
                      {weekDays.map(day => (
                        <Panel 
                          header={
                            <span>
                              <span style={{ marginRight: 8 }}>{day.icon}</span>
                              {day.label}
                            </span>
                          } 
                          key={day.key}
                        >
                          <Form.List name={['schedule', day.key]}>
                            {(fields, { add, remove }) => (
                              <>
                                {fields.map(({ key, name, ...restField }) => (
                                  <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                                    <Col span={10}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'from']}
                                        rules={[{ required: true, message: t('schedule.pleaseSelectStartTime') }]}
                                      >
                                        <Select placeholder={t('schedule.startTime')}>
                                          {timeSlots.map(time => (
                                            <Option key={time} value={time}>{time}</Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                    <Col span={10}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'to']}
                                        rules={[{ required: true, message: t('schedule.pleaseSelectEndTime') }]}
                                      >
                                        <Select placeholder={t('schedule.endTime')}>
                                          {timeSlots.map(time => (
                                            <Option key={time} value={time}>{time}</Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                      <Button 
                                        type="text" 
                                        danger 
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => remove(name)}
                                      />
                                    </Col>
                                  </Row>
                                ))}
                                <Button 
                                  type="dashed" 
                                  onClick={() => add()} 
                                  block 
                                  icon={<PlusOutlined />}
                                >
                                  {t('schedule.addTimeSlot')}
                                </Button>
                              </>
                            )}
                          </Form.List>
                        </Panel>
                      ))}
                    </Collapse>
                  </Form.Item>
                </TabPane>

                <TabPane tab={t('schedule.appointmentSettings')} key="appointments">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.defaultAppointmentDuration')}
                        name="default_appointment_duration"
                        rules={[{ required: true, message: t('schedule.pleaseInputDuration') }]}
                      >
                        <InputNumber 
                          min={5} 
                          max={120} 
                          style={{ width: '100%' }} 
                          addonAfter={t('schedule.minutes')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.maxAppointmentsPerDay')}
                        name="max_appointments_per_day"
                        rules={[{ required: true, message: t('schedule.pleaseInputMaxAppointments') }]}
                      >
                        <InputNumber 
                          min={1} 
                          max={50} 
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.advanceBookingDays')}
                        name="advance_booking_days"
                        rules={[{ required: true, message: t('schedule.pleaseInputAdvanceDays') }]}
                      >
                        <InputNumber 
                          min={0} 
                          max={365} 
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.minNoticeHours')}
                        name="min_notice_hours"
                        rules={[{ required: true, message: t('schedule.pleaseInputMinNotice') }]}
                      >
                        <InputNumber 
                          min={0} 
                          max={48} 
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.cancellationHours')}
                        name="cancellation_hours"
                        rules={[{ required: true, message: t('schedule.pleaseInputCancellation') }]}
                      >
                        <InputNumber 
                          min={0} 
                          max={72} 
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.rescheduleHours')}
                        name="reschedule_hours"
                        rules={[{ required: true, message: t('schedule.pleaseInputReschedule') }]}
                      >
                        <InputNumber 
                          min={0} 
                          max={72} 
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.availability')}
                        name="is_available_for_walk_in"
                        valuePropName="checked"
                      >
                        <Switch checkedChildren={t('schedule.available')} unCheckedChildren={t('schedule.unavailable')} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('schedule.telemedicine')}
                        name="is_available_for_telemedicine"
                        valuePropName="checked"
                      >
                        <Switch checkedChildren={t('schedule.available')} unCheckedChildren={t('schedule.unavailable')} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={t('schedule.sameDayBooking')}
                    name="same_day_booking"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren={t('common.yes')} unCheckedChildren={t('common.no')} />
                  </Form.Item>
                </TabPane>
              </Tabs>

              <div style={{ textAlign: 'right', marginTop: 24 }}>
                <Space>
                  <Button onClick={() => {
                    setEditingSchedule(null);
                    form.resetFields();
                  }} disabled={isSubmitting}>
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={isSubmitting}
                    icon={<SaveOutlined />}
                  >
                    {editingSchedule ? t('schedule.updateSchedule') : t('schedule.createSchedule')}
                  </Button>
                </Space>
              </div>
            </Form>
          </Card>
        </Col>

        {/* Existing Schedules */}
        <Col xs={24} lg={8}>
          <Card title={t('schedule.existingSchedules')}>
            {doctorProviders.map((provider: Provider) => (
              <div key={provider.id}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>{provider.clinic.internal_name || provider.clinic.name_zh_hk || provider.clinic.name_en}</Text>
                  <br />
                  <Text type="secondary">{provider.clinic.name_zh_hk}</Text>
                </div>
                
                {provider.schedules.length === 0 ? (
                  <Alert
                    message={t('schedule.noSchedules')}
                    type="info"
                    showIcon
                  />
                ) : (
                  <div>
                    {provider.schedules.map((schedule: ProviderSchedule) => (
                      <Card 
                        key={schedule.id} 
                        size="small" 
                        style={{ marginBottom: 8 }}
                        actions={[
                          <Tooltip title={t('common.edit')}>
                            <Button 
                              type="text" 
                              icon={<SettingOutlined />}
                              onClick={() => handleEditSchedule(schedule)}
                            />
                          </Tooltip>,
                          <Popconfirm
                            title={t('schedule.deleteConfirm')}
                            onConfirm={() => handleDeleteSchedule(schedule.id)}
                            okText={t('common.yes')}
                            cancelText={t('common.no')}
                          >
                            <Tooltip title={t('common.delete')}>
                              <Button type="text" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                          </Popconfirm>
                        ]}
                      >
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            {getStatusTag(schedule.status)}
                            <Text type="secondary">Priority: {schedule.priority}</Text>
                          </div>
                          
                          {schedule.from_date && (
                            <div style={{ marginBottom: 4 }}>
                              <CalendarOutlined style={{ marginRight: 4 }} />
                              <Text type="secondary">
                                {dayjs(schedule.from_date).format('YYYY-MM-DD')}
                                {schedule.to_date && ` - ${dayjs(schedule.to_date).format('YYYY-MM-DD')}`}
                              </Text>
                            </div>
                          )}
                          
                          <div style={{ marginBottom: 4 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            <Text type="secondary">
                              {schedule.default_appointment_duration}min / {schedule.max_appointments_per_day} max/day
                            </Text>
                          </div>
                          
                          <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">
                              {schedule.is_available_for_telemedicine ? '📱' : '🏥'} 
                              {schedule.is_available_for_walk_in ? '🚶' : ''} 
                              {schedule.same_day_booking ? '⚡' : ''}
                            </Text>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DoctorSchedule; 